import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../store";
import {
  getReportDifference,
  getReportHistory,
  pullPeopleReport,
  pullPropertyReport,
  searchPeople,
  socialSearch,
  pullPhoneReport,
  getRentData,
  submitComplaint,
  pullVehicleReport,
  pullVehicleMarketEstimate,
  pullNeighborhoodReportByLocation,
  pullNeighborhoodSearch,
  propertyReportDifference,
  getNeighborhoodPOIs,
  getAllLists,
  getSpecificList,
  addReportToList,
  removeReportFromList,
  renameList,
  deleteList,
  getAllReportsWithNotes,
  getSpecificReportNotes,
  addReportNote,
  removeReportNote,
  updateReportNote,
  preForeclosureSearch,
  recentSaleSearch,
} from "../reports/reportsAPI";
import {
  PeopleReportHistory,
  PhoneReportHistory,
  PropertyReportHistory,
  SocialReportHistory,
  PullPeopleReportParams,
  PullPropertyReportParams,
  PeopleReport,
  PropertyReport,
  SearchPeopleReportParams,
  TeaserReport,
  SocialReportType,
  SocialSearch,
  GetReportDifferenceParams,
  MonitoringDifference,
  PhoneReport,
  PullPhoneReportParams,
  GetRentDataParams,
  RentData,
  SubmitComplaintParams,
  VehicleReportType,
  VehicleReportHistory,
  PullVehicleReportParams,
  PullVehicleMarketEstimateParams,
  PropertyName,
  PropertyReportDifferenceParams,
  PropertyReportDifference,
  GetPropertyPOIsParams,
  ReportList,
  GetSpecificListParams,
  RemoveReportFromListParams,
  RenameListParams,
  DeleteListParams,
  CombinedAddReportToList,
  ReportWithNote,
  ReportNote,
  CombinedNoteParams,
  RemoveReportNoteParams,
  UpdateReportNoteParams,
  CombinedReportWithNote,
  VehicleValuation,
  preForeclosureSearchParams,
  preForeclosureData,
  recentSalesData,
  recentSaleSearchParams,
} from "./reportTypes/";

import { logoutAsync } from "../authentication/authenticationSlice";
import {
  formatName,
  scoreGenerator,
  setValuationSummary,
  checkIfReportIsOnList,
  validateStateAbbreviation,
} from "../../utils";
import {
  NeighborhoodReport,
  NeighborhoodReportHistory,
  PullNeighborhoodReportParams,
  PullNeighborhoodSearchParams,
  NeighborhoodMapModalContent,
} from "./reportTypes/neighborhoodReportTypes";

export type CombinedReports =
  | PeopleReport
  | PhoneReport
  | PropertyReport
  | SocialReportType
  | VehicleReportType
  | NeighborhoodReport
  | preForeclosureData
  | recentSalesData;

export interface ReportsState {
  status: "idle" | "loading" | "failed"; // default status should personalize each
  reportHistoryStatus: "idle" | "loading" | "failed";
  peopleReportStatus: "idle" | "loading" | "failed";
  peopleSearchStatus: "idle" | "loading" | "failed";
  propertyReportStatus: "idle" | "loading" | "failed";
  aiStatus: "idle" | "loading" | "failed";
  socialSearchStatus: "idle" | "loading" | "failed";
  phoneReportStatus: "idle" | "loading" | "failed";
  vehicleReportStatus: "idle" | "loading" | "failed";
  vehicleMarketEstimationStatus: "idle" | "loading" | "failed";
  neighborhoodReportStatus: "idle" | "loading" | "failed";
  neighborhoodSearchStatus: "idle" | "loading" | "failed";
  rentDataStatus: "idle" | "loading" | "failed";
  submitComplaintStatus: "idle" | "loading" | "failed";
  listStatus: "idle" | "loading" | "failed";
  notesStatus: "idle" | "loading" | "failed";
  preForeclosureSearchStatus: "idle" | "loading" | "failed";
  recentSaleSearchStatus: "idle" | "loading" | "failed";
  allReportHistory?: Array<
    | PeopleReportHistory
    | PhoneReportHistory
    | PropertyReportHistory
    | SocialReportHistory
    | VehicleReportHistory
    | NeighborhoodReportHistory
  >;
  peopleReportHistory?: PeopleReportHistory[];
  phoneReportHistory?: PhoneReportHistory[];
  propertyReportHistory?: PropertyReportHistory[];
  socialReportHistory?: SocialReportHistory[];
  vehicleReportHistory?: VehicleReportHistory[];
  neighborhoodReportHistory?: NeighborhoodReportHistory[];
  mostRecentReport?:
    | PeopleReportHistory
    | PhoneReportHistory
    | PropertyReportHistory
    | SocialReportHistory
    | VehicleReportHistory
    | NeighborhoodReportHistory
    | null;
  peopleReport?: PeopleReport;
  peopleReportMonitoringDifferences?: MonitoringDifference;
  propertyReport?: PropertyReport;
  propertyReportTrackingFlags: {
    neighborhoodAiUsed: boolean;
    neighborhoodAiVoted: boolean;
  };
  propertyReportMonitoringDifferences?: PropertyReportDifference;
  socialReport?: SocialReportType;
  phoneReport?: PhoneReport;
  vehicleReport?: VehicleReportType;
  neighborhoodReport?: NeighborhoodReport;
  neighborhoodSearch?: any;
  isReportHistoryEmpty: boolean;
  peopleTeaserReports?: TeaserReport[];
  rentData?: RentData;
  peopleReportMonitoringModal?: boolean;
  neighborhoodMapModal: boolean;
  preSiteJabberModalShown: boolean;
  showSiteJabberFromFeedback: boolean;
  siteJabberShown: boolean;
  neighborhoodMapModalContent?: NeighborhoodMapModalContent;
  reportLists?: ReportList[];
  allReportNotesList?: CombinedReportWithNote[];
  specificReportNotes?: ReportNote[];
  preForeclosureData?: preForeclosureData[];
  propertyReportPreForeclosureData?: preForeclosureData[];
  propertyReportRecentSalesData?: recentSalesData[];
  preForeclosureSearchType:
    | "MultiSearch"
    | "MapSearch"
    | "property report"
    | "";
  vehicleNoResultsModalShown: boolean;
  searchMethod?: "Google" | "Manual" | "LinkClick" | undefined;
}

const initialState: ReportsState = {
  status: "idle",
  reportHistoryStatus: "idle",
  peopleReportStatus: "idle",
  peopleSearchStatus: "idle",
  propertyReportStatus: "idle",
  preForeclosureSearchStatus: "idle",
  recentSaleSearchStatus: "idle",
  aiStatus: "idle",
  socialSearchStatus: "idle",
  phoneReportStatus: "idle",
  vehicleReportStatus: "idle",
  vehicleMarketEstimationStatus: "idle",
  neighborhoodReportStatus: "idle",
  neighborhoodSearchStatus: "idle",
  rentDataStatus: "idle",
  listStatus: "idle",
  notesStatus: "idle",
  submitComplaintStatus: "idle",
  isReportHistoryEmpty: true,
  neighborhoodMapModal: false,
  preSiteJabberModalShown: false,
  showSiteJabberFromFeedback: false,
  siteJabberShown: false,
  propertyReportTrackingFlags: {
    neighborhoodAiUsed: false,
    neighborhoodAiVoted: false,
  },
  preForeclosureSearchType: "",
  vehicleNoResultsModalShown: false,
};

export const getReportHistoryAsync = createAsyncThunk<any, void, {}>(
  "report/getReportHistory",
  async () => {
    return getReportHistory();
  },
);

/* People Report */
export const searchPeopleAsync = createAsyncThunk<
  any,
  SearchPeopleReportParams,
  {}
>("report/searchPeople", async params => {
  return searchPeople(params);
});

export const pullPeopleReportAsync = createAsyncThunk<
  any,
  PullPeopleReportParams,
  {}
>("report/pullPeopleReport", async params => {
  return pullPeopleReport(params);
});

export const getReportDifferenceAsync = createAsyncThunk<
  any,
  GetReportDifferenceParams,
  {}
>("report/getReportDifference", async params => {
  return getReportDifference(params);
});

/* Property Report */
export const pullPropertyReportAsync = createAsyncThunk<
  any,
  PullPropertyReportParams,
  {}
>("report/pullPropertyReport", async params => {
  return pullPropertyReport(params);
});

export const propertyReportDifferenceAsync = createAsyncThunk<
  any,
  PropertyReportDifferenceParams,
  {}
>("monitoring/propertyReportDifference", async address => {
  return propertyReportDifference(address);
});

export const getNeighborhoodPOIsAsync = createAsyncThunk<
  any,
  GetPropertyPOIsParams,
  {}
>("report/getNeighborhoodPOIs", async address => {
  return getNeighborhoodPOIs(address);
});

export const getRentDataAsync = createAsyncThunk<any, GetRentDataParams, {}>(
  "report/getRentData",
  async params => {
    return getRentData(params);
  },
);

/* Social Report */
export const searchSocialAsync = createAsyncThunk<any, SocialSearch, {}>(
  "report/searchSocial",
  async params => {
    return socialSearch(params);
  },
);

/* Phone Report */
export const pullPhoneReportAsync = createAsyncThunk<
  any,
  PullPhoneReportParams,
  {}
>("report/pullPhoneReport", async params => {
  return pullPhoneReport(params);
});

export const submitComplaintAsync = createAsyncThunk<
  any,
  SubmitComplaintParams,
  {}
>("report/submitComplaint", async params => {
  return submitComplaint(params);
});

/* Vehicle Report */
export const pullVehicleReportAsync = createAsyncThunk<
  any,
  PullVehicleReportParams,
  {}
>("report/pullVehicleReport", async params => {
  return pullVehicleReport(params);
});

export const pullVehicleMarketEstimateAsync = createAsyncThunk<
  any,
  PullVehicleMarketEstimateParams,
  {}
>("report/pullVehicleMarketEstimate", async params => {
  return pullVehicleMarketEstimate(params);
});

/* Neighborhood Report */
export const pullNeighborhoodReportByLocationAsync = createAsyncThunk<
  any,
  PullNeighborhoodReportParams,
  {}
>("report/pullNeighborhoodReportByLocation", async params => {
  return pullNeighborhoodReportByLocation(params);
});

export const pullNeighborhoodSearchAsync = createAsyncThunk<
  any,
  PullNeighborhoodSearchParams,
  {}
>("report/pullNeighborhoodSearch", async params => {
  return pullNeighborhoodSearch(params);
});

/* List Actions */
export const getAllListsAsync = createAsyncThunk<any, void, {}>(
  "report/getAllLists",
  async () => {
    return getAllLists();
  },
);

export const getSpecificListAsync = createAsyncThunk<
  any,
  GetSpecificListParams,
  {}
>("report/getSpecificList", async params => {
  return getSpecificList(params);
});

export const addReportToListAsync = createAsyncThunk<
  any,
  CombinedAddReportToList,
  {}
>("report/addReportToList", async params => {
  return addReportToList(params);
});

export const removeReportFromListAsync = createAsyncThunk<
  any,
  RemoveReportFromListParams,
  {}
>("report/removeReportFromList", async params => {
  return removeReportFromList(params);
});

export const renameListAsync = createAsyncThunk<any, RenameListParams, {}>(
  "report/renameList",
  async params => {
    return renameList(params);
  },
);

export const deleteListAsync = createAsyncThunk<any, DeleteListParams, {}>(
  "report/deleteList",
  async params => {
    return deleteList(params);
  },
);

/* Note Actions */
export const getAllReportsWithNotesAsync = createAsyncThunk<any, void, {}>(
  "report/getAllReportsWithNotes",
  async () => {
    return getAllReportsWithNotes();
  },
);

export const getSpecificReportNotesAsync = createAsyncThunk<
  any,
  CombinedNoteParams,
  {}
>("report/getSpecificReportNotes", async params => {
  return getSpecificReportNotes(params);
});

export const addReportNoteAsync = createAsyncThunk<any, CombinedNoteParams, {}>(
  "report/addReportNote",
  async params => {
    return addReportNote(params);
  },
);

export const removeReportNoteAsync = createAsyncThunk<
  any,
  RemoveReportNoteParams,
  {}
>("report/removeReportNote", async params => {
  return removeReportNote(params);
});

export const updateReportNoteAsync = createAsyncThunk<
  any,
  UpdateReportNoteParams,
  {}
>("report/updateReportNote", async params => {
  return updateReportNote(params);
});

/* Market Search */
export const preForeclosureSearchAsync = createAsyncThunk<
  any,
  preForeclosureSearchParams,
  {}
>("report/preForeclosureSearch", async (params, thunkApi) => {
  // only property report passes directly thru
  // other methods need to get the preForeclosureSearchType from state due to search context
  let preForeclosureSearchType = params.preForeclosureSearchType;
  if (!preForeclosureSearchType) {
    const state = thunkApi.getState() as RootState;
    preForeclosureSearchType = state.reports.preForeclosureSearchType;
  }
  const updatedParams: preForeclosureSearchParams = {
    ...params,
    preForeclosureSearchType,
  };
  return preForeclosureSearch(updatedParams);
});

export const recentSaleSearchAsync = createAsyncThunk<
  any,
  recentSaleSearchParams,
  {}
>("report/recentSaleSearch", async params => {
  return recentSaleSearch(params);
});

/* Slice */
export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setMostRecentReport: (state, action) => {
      if (state.isReportHistoryEmpty) {
        state.isReportHistoryEmpty = false;
      }
      state.mostRecentReport = action.payload;
    },
    setPeopleReportMonitoringModal: (state, action) => {
      state.peopleReportMonitoringModal = action.payload;
    },
    setNeighborhoodMapModal: (state, action) => {
      state.neighborhoodMapModal = action.payload;
    },
    setNeighborhoodMapModalContent: (state, action) => {
      let offender = action.payload.offender;
      let index = action.payload.index;
      state.neighborhoodMapModalContent = {
        offender,
        index,
      };
    },
    setSiteJabberShown: state => {
      state.siteJabberShown = true;
    },
    setPreSiteJabberModalShown: state => {
      state.preSiteJabberModalShown = true;
    },
    setShowSiteJabberFromFeedback: state => {
      state.showSiteJabberFromFeedback = true;
    },
    updateNeighborhoodAiUsed: state => {
      state.propertyReportTrackingFlags.neighborhoodAiUsed = true;
    },
    updateNeighborhoodAiVoted: state => {
      state.propertyReportTrackingFlags.neighborhoodAiVoted = true;
    },
    clearSpecificReportNotes: state => {
      state.specificReportNotes = undefined;
    },
    clearPeopleTeaserReports: state => {
      state.peopleTeaserReports = undefined;
    },
    setPreForeclosureSearchType: (state, action) => {
      state.preForeclosureSearchType = action.payload;
    },
    setVehicleNoResultsModalShown: state => {
      state.vehicleNoResultsModalShown = true;
    },
    setPropertyReportPreForeclosureData: state => {
      state.propertyReportPreForeclosureData = [];
    },
    setPropertyReportRecentSalesData: state => {
      state.propertyReportRecentSalesData = [];
    },
  },
  extraReducers: builder => {
    builder
      /* getReportHistoryAsync */
      .addCase(getReportHistoryAsync.pending, state => {
        state.reportHistoryStatus = "loading";
      })
      .addCase(getReportHistoryAsync.fulfilled, (state, action) => {
        state.reportHistoryStatus = "idle";
        state.mostRecentReport = null;

        state.allReportHistory = action.payload.allResults;

        if (action.payload.allResults && action.payload.allResults.length > 0) {
          state.isReportHistoryEmpty = false;
          // set the most recent report to the first element in the array (sorted in decending order)
          state.mostRecentReport = action.payload.allResults[0];
        } else {
          state.isReportHistoryEmpty = true;
        }

        state.peopleReportHistory = action.payload.peopleReportHistory;
        state.phoneReportHistory = action.payload.phoneReportHistory;
        state.propertyReportHistory = action.payload.propertyReportHistory;
        state.socialReportHistory = action.payload.socialReportHistory;
        state.vehicleReportHistory = action.payload.vehicleReportHistory;
        state.neighborhoodReportHistory = action.payload.neighborhoodHistory;
      })
      .addCase(getReportHistoryAsync.rejected, state => {
        state.reportHistoryStatus = "failed";
      })
      /* handle logout */
      .addCase(logoutAsync.pending, () => {})
      .addCase(logoutAsync.fulfilled, state => {
        state.allReportHistory = undefined;
        state.peopleReportHistory = undefined;
        state.phoneReportHistory = undefined;
        state.propertyReportHistory = undefined;
        state.socialReportHistory = undefined;
        state.mostRecentReport = null;
        state.isReportHistoryEmpty = true;
        state.propertyReportStatus = "idle";
        state.aiStatus = "idle";
        state.peopleReportStatus = "idle";
        state.phoneReportStatus = "idle";
        state.socialSearchStatus = "idle";
        state.peopleSearchStatus = "idle";
        state.vehicleReportStatus = "idle";
        state.neighborhoodReportStatus = "idle";
        state.rentDataStatus = "idle";
        state.submitComplaintStatus = "idle";
        state.listStatus = "idle";
        state.peopleReport = undefined;
        state.propertyReport = undefined;
        state.phoneReport = undefined;
        state.socialReport = undefined;
        state.rentData = undefined;
        state.vehicleReport = undefined;
        state.neighborhoodReport = undefined;
        state.neighborhoodSearch = undefined;
        state.peopleReportMonitoringDifferences = undefined;
        state.peopleTeaserReports = undefined;
        state.siteJabberShown = false;
        state.preSiteJabberModalShown = false;
        state.showSiteJabberFromFeedback = false;
        state.propertyReportTrackingFlags = {
          neighborhoodAiUsed: false,
          neighborhoodAiVoted: false,
        };
        state.vehicleNoResultsModalShown = false;
      })
      .addCase(logoutAsync.rejected, state => {
        state.reportHistoryStatus = "failed";
      })
      /* searchPeopleAsync */
      .addCase(searchPeopleAsync.pending, state => {
        state.peopleSearchStatus = "loading";
      })
      .addCase(searchPeopleAsync.fulfilled, (state, action) => {
        state.peopleSearchStatus = "idle";
        state.peopleTeaserReports = action.payload.searchResponse;
      })
      .addCase(searchPeopleAsync.rejected, state => {
        state.peopleSearchStatus = "failed";
      })
      /* pullPeopleReportAsync */
      .addCase(pullPeopleReportAsync.pending, state => {
        state.peopleReportStatus = "loading";
        state.specificReportNotes = undefined;
      })
      .addCase(pullPeopleReportAsync.fulfilled, (state, action) => {
        state.peopleReport = undefined;
        state.peopleReportMonitoringDifferences = undefined;
        state.peopleReportStatus = "idle";
        if (action.payload.report.result) {
          let report = action.payload.report.result as PeopleReport;

          // set report to peopleReport
          state.peopleReport = report;

          // add report to the peopleReportHistory & allHistory
          let newHistoryReport: PeopleReportHistory = {
            age: report.age,
            city: "",
            dateOfBirth: report.dateOfBirth,
            dateOfDeath: report.dateOfDeath,
            ageAtDeath: report.ageAtDeath,
            deceased: report.dateOfDeath ? "Yes" : "No",
            firstName: report.firstName,
            fullAddress: "",
            gender: report.gender,
            lastName: report.lastName,
            middleName: report.middleName,
            monitoring: false,
            origin: "Membersite",
            reportToken: report.reportToken!,
            state: "",
            version: report.reportSummary.version!,
            zip: "",
            score: scoreGenerator(),
            type: "people",
            schema: "v2",
          };
          if (report.addresses.length > 0) {
            newHistoryReport.city = report.addresses[0].city;
            newHistoryReport.fullAddress = report.addresses[0].fullAddress;
            newHistoryReport.state = report.addresses[0].state;
            newHistoryReport.zip = report.addresses[0].postalCode;
          }

          // set most recent report to the report
          state.mostRecentReport = newHistoryReport;

          state.peopleReportHistory?.unshift(newHistoryReport);

          state.allReportHistory?.unshift(newHistoryReport);
        }
      })
      .addCase(pullPeopleReportAsync.rejected, state => {
        state.peopleReport = undefined;
        state.peopleReportStatus = "failed";
      })
      /* searchSocialAsync */
      .addCase(searchSocialAsync.pending, state => {
        state.socialSearchStatus = "loading";
      })
      .addCase(searchSocialAsync.fulfilled, (state, action) => {
        state.socialReport = undefined;
        state.socialSearchStatus = "idle";
        if (!action.payload.noResults) {
          state.socialReport = action.payload.socialProfileSearchResponse
            .hasError
            ? undefined
            : action.payload.socialProfileSearchResponse;

          if (
            action.payload.socialProfileSearchResponse &&
            action.payload.socialProfileSearchResponse.socialReportSummary &&
            action.payload.socialProfileSearchResponse.socialReportSummary
              .numberOfResults > 0
          ) {
            let newHistoryReport: SocialReportHistory = {
              score: scoreGenerator(),
              type: "social",
              twitter:
                action.payload.socialProfileSearchResponse.socialReportSummary
                  .twitter,
              email:
                action.payload.socialProfileSearchResponse.socialReportSummary
                  .email,
              fullName: action.payload.socialProfileSearchResponse.fullName,
              gender: action.payload.socialProfileSearchResponse.gender,
              phone:
                action.payload.socialProfileSearchResponse.socialReportSummary
                  .phone,
              location: action.payload.socialProfileSearchResponse.location,
              schema: "v2",
            };

            state.mostRecentReport = newHistoryReport;
            state.socialReportHistory?.unshift(newHistoryReport);
            state.allReportHistory?.unshift(newHistoryReport);
          }
        }
      })
      .addCase(searchSocialAsync.rejected, state => {
        state.socialReport = undefined;
        state.socialSearchStatus = "failed";
      })
      /* getReportDifferenceAsync */
      .addCase(getReportDifferenceAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(getReportDifferenceAsync.fulfilled, (state, action) => {
        state.status = "idle";
        if (action.payload.success && action.payload.reportDifference) {
          const reportDifference = action.payload
            .reportDifference as MonitoringDifference;
          state.peopleReportMonitoringDifferences = {
            ...reportDifference,
            contactTitle:
              reportDifference.addresses ||
              reportDifference.emailAddresses ||
              reportDifference.phoneNumbers
                ? true
                : false,
            peopleTitle:
              reportDifference.relatives ||
              reportDifference.associates ||
              reportDifference.neighbors
                ? true
                : false,
            criminalTitle: reportDifference.criminalRecords ? true : false,
            financialTitle:
              reportDifference.bankruptcies ||
              reportDifference.judgments ||
              reportDifference.liens ||
              reportDifference.propertyForeclosures ||
              reportDifference.evictions ||
              reportDifference.uccFilings
                ? true
                : false,
            careerTitle:
              reportDifference.employers ||
              reportDifference.corporateAffiliations ||
              reportDifference.businessAffiliations ||
              reportDifference.professionalLicenses
                ? true
                : false,
            lifestyleTitle:
              reportDifference.voterRegistrations ||
              reportDifference.huntingPermits ||
              reportDifference.driversLicenses ||
              reportDifference.pilotLicenses ||
              reportDifference.vehicles ||
              reportDifference.concealedWeaponPermits
                ? true
                : false,
            anyReportDifference: Object.values(reportDifference).some(
              value => !!value,
            ),
          };
        }
      })
      .addCase(getReportDifferenceAsync.rejected, state => {
        state.status = "failed";
      })
      /* pullPhoneReportAsync */
      .addCase(pullPhoneReportAsync.pending, (state, action) => {
        state.phoneReportStatus = "loading";
        state.specificReportNotes = undefined;
      })
      .addCase(pullPhoneReportAsync.rejected, state => {
        state.phoneReport = undefined;
        state.phoneReportStatus = "failed";
      })
      .addCase(pullPhoneReportAsync.fulfilled, (state, action) => {
        state.phoneReport = undefined;
        state.phoneReportStatus = "idle";
        let report = action.payload.report as PhoneReport;

        // set report to phoneReport state
        state.phoneReport = report;
        // need to add state setting for most recent report
        let newHistoryReport: PhoneReportHistory = {
          carrier: report.phoneReportSummary.carrier,
          phone: report.searchCriteria.phone,
          phoneCity: report.phoneReportSummary.phoneCity,
          phoneState: report.phoneReportSummary.phoneState,
          phoneType: report.phoneReportSummary.phoneType,
          score: scoreGenerator(),
          type: "phone",
          schema: "v2",
        };

        //if people report options
        newHistoryReport.personCity = report.results.people[0]?.address[0]?.city
          ? report.results.people[0]?.address[0]?.city
          : undefined;

        newHistoryReport.personFullAddress = report.phoneReportSummary
          .personAddress
          ? report.phoneReportSummary.personAddress
          : undefined;

        newHistoryReport.personState = report.results.people[0]?.address[0]
          .state
          ? report.results.people[0]?.address[0].state
          : undefined;
        newHistoryReport.personZip = report.results.people[0]?.address[0]
          ?.postalCode
          ? report.results.people[0]?.address[0]?.postalCode
          : undefined;

        // if business report options
        newHistoryReport.businessCity = report.results.businesses[0]?.address
          ?.city
          ? report.results.businesses[0]?.address?.city
          : undefined;

        newHistoryReport.businessState = report.results.businesses[0]?.address
          ?.state
          ? report.results.businesses[0]?.address?.state
          : undefined;

        newHistoryReport.businessFullAddress = report.results.businesses[0]
          ?.address?.fullAddress
          ? report.results.businesses[0]?.address?.fullAddress
          : undefined;

        newHistoryReport.businessZip = report.results.businesses[0]?.address
          ?.zip4
          ? report.results.businesses[0]?.address?.zip4
          : undefined;

        state.mostRecentReport = newHistoryReport;

        state.phoneReportHistory?.unshift(newHistoryReport);
        state.allReportHistory?.unshift(newHistoryReport);
      })
      /* pullPropertyReportAsync */
      .addCase(pullPropertyReportAsync.pending, (state, action) => {
        state.propertyReportStatus = "loading";
        state.specificReportNotes = undefined;
      })
      .addCase(pullPropertyReportAsync.rejected, state => {
        state.propertyReport = undefined;
        state.propertyReportTrackingFlags = {
          neighborhoodAiUsed: false,
          neighborhoodAiVoted: false,
        };
        state.propertyReportStatus = "failed";
      })
      .addCase(pullPropertyReportAsync.fulfilled, (state, action) => {
        state.propertyReport = undefined;
        state.propertyReportTrackingFlags = {
          neighborhoodAiUsed: false,
          neighborhoodAiVoted: false,
        };
        state.propertyReportMonitoringDifferences = undefined;
        state.propertyReportStatus = "idle";
        if (!action.payload.noResults) {
          let report = action.payload.report.results
            .scalableReport as PropertyReport;

          // set report to propertyReport state
          state.propertyReport = report;

          //checking for opt outs and null results
          if (report) {
            // need to add state setting for most recent report
            let newHistoryReport: PropertyReportHistory = {
              acres: report.propertyDetails?.acres,
              addressLine1: report.propertyDetails?.fullAddress,
              propertyDetails: report.propertyDetails,
              bedrooms: report.propertyDetails?.bedrooms,
              buildingSquareFeet: report.propertyDetails?.buildingSquareFeet,
              city: report.propertyDetails?.city,
              latitude: report.propertyDetails?.latitude,
              longitude: report.propertyDetails?.longitude,
              state: report.propertyDetails?.state,
              totalBaths: report.propertyDetails?.totalBaths,
              zip: report.propertyDetails?.postalCode,
              score: scoreGenerator(),
              type: "property",
              schema: "v3",
              currentOwners: {
                owners: report.propertyCurrentOwnership?.names?.map(
                  (name: PropertyName) => {
                    return {
                      fullName: name.firstName
                        ? formatName(name)
                        : name.businessName
                          ? name.businessName
                          : "",
                      reportToken: name.reportToken,
                    };
                  },
                ),
                saleDate: report.propertyCurrentOwnership?.saleDate,
              },
              valuationSummary: setValuationSummary(report),
              propertyId: report.propertyReportSummary?.hasPreforeclosureDetails
                ? report.propertyReportSummary?.firstAmericanPropertyId
                : undefined, // Optional property ID for FA data
            };

            // set most recent report to the report
            state.mostRecentReport = newHistoryReport;

            state.propertyReportHistory?.unshift(newHistoryReport);
            state.allReportHistory?.unshift(newHistoryReport);
          }
        }
      })
      /* propertyReportDifferenceAsync */
      .addCase(propertyReportDifferenceAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(propertyReportDifferenceAsync.rejected, state => {
        state.propertyReportMonitoringDifferences = undefined;
        state.status = "failed";
      })
      .addCase(propertyReportDifferenceAsync.fulfilled, (state, action) => {
        state.propertyReportMonitoringDifferences = action.payload;
        state.status = "idle";
      })
      /* getNeighborhoodPOIsAsync */
      .addCase(getNeighborhoodPOIsAsync.pending, state => {
        state.aiStatus = "loading";
      })
      .addCase(getNeighborhoodPOIsAsync.rejected, state => {
        state.aiStatus = "failed";
      })
      .addCase(getNeighborhoodPOIsAsync.fulfilled, (state, action) => {
        state.aiStatus = "idle";
        state.propertyReport = {
          ...state.propertyReport,
          neighborhoodAi: action.payload,
        };
      })
      /* getRentDataAsync */
      .addCase(getRentDataAsync.pending, (state, action) => {
        state.rentDataStatus = "loading";
      })
      .addCase(getRentDataAsync.rejected, state => {
        state.rentDataStatus = "failed";
      })
      .addCase(getRentDataAsync.fulfilled, (state, action) => {
        state.rentDataStatus = "idle";

        let report = action.payload.rentDataResponse.results
          .scalableReport as RentData;

        state.rentData = report;
      })
      /* pullMarketSearch */
      .addCase(preForeclosureSearchAsync.pending, state => {
        state.preForeclosureSearchStatus = "loading";
      })
      .addCase(preForeclosureSearchAsync.rejected, state => {
        state.preForeclosureSearchStatus = "failed";
        state.preForeclosureSearchType = "";
      })
      .addCase(preForeclosureSearchAsync.fulfilled, (state, action) => {
        state.preForeclosureSearchStatus = "idle";
        state.preForeclosureSearchType = "";
        const result = action.payload.preForeclosureSearchResponse
          .result as preForeclosureData[];
        const context = action.meta?.arg?.context;
        if (context === "property") {
          state.propertyReportPreForeclosureData = result;
        } else {
          state.preForeclosureData = result;
        }
        // Monkey patch for adding to list. Needs to be fixed properly in backend.
        // either add type of preforeclosure to be added to list
        // or have preforeclosure data pull return this structure to fit list for property type
        // commenting out for now, add to list feature for this temp disabled.
        // state.preForeclosureData?.map(report => {
        //   report.reportType = "property";
        //   report.propertyDetails = {};
        //   report.propertyDetails.addressLine1 = report.fullStreetAddress;
        //   report.propertyDetails.city = report.city;
        //   report.propertyDetails.state = report.state;
        //   report.propertyDetails.postalCode = report.zip5;
        //   return report;
        // });
      })
      // pullrecentSaleSearch
      .addCase(recentSaleSearchAsync.pending, state => {
        state.recentSaleSearchStatus = "loading";
      })
      .addCase(recentSaleSearchAsync.rejected, state => {
        state.recentSaleSearchStatus = "failed";
      })
      .addCase(recentSaleSearchAsync.fulfilled, (state, action) => {
        state.recentSaleSearchStatus = "idle";
        const result = action.payload.recentSaleSearchResponse
          .result as recentSalesData[];
        state.propertyReportRecentSalesData = result;
      })
      /* pullVehicleReportAsync */
      .addCase(pullVehicleReportAsync.pending, state => {
        state.vehicleReportStatus = "loading";
        state.specificReportNotes = undefined;
      })
      .addCase(pullVehicleReportAsync.fulfilled, (state, action) => {
        state.vehicleReport = undefined;
        state.vehicleReportStatus = "idle";

        let vehicleReport = action.payload.report as VehicleReportType;
        // for activity history and most recent report
        // will get saved to local state but not to the database
        if (
          !vehicleReport.vehicleSummary.plateNumber &&
          action.meta.arg.plate
        ) {
          vehicleReport.vehicleSummary.plateNumber = action.meta.arg.plate;
        }
        if (!vehicleReport.vehicleSummary.plateState && action.meta.arg.state) {
          vehicleReport.vehicleSummary.plateState = action.meta.arg.state;
        }

        state.vehicleReport = vehicleReport;

        if (vehicleReport) {
          let newHistoryReport: VehicleReportHistory = {
            score: scoreGenerator(),
            type: "vehicle",
            schema: "v2",
            vin: vehicleReport.vehicleSummary.vin,
            vehicleDescription: vehicleReport.vehicleSummary.vehicleDescription,
            msrp: vehicleReport.vehicleSummary.msrp,
            titleCount: vehicleReport.vehicleSummary.titleCount,
            lastKnownState: vehicleReport.vehicleTitle
              ?.currentTitleInformation[0]
              ? validateStateAbbreviation(
                  vehicleReport.vehicleTitle?.currentTitleInformation[0]
                    .titleIssuingAuthorityName,
                )
              : "Unknown",
          };

          if (
            vehicleReport.vehicleSummary.plateNumber ||
            action.meta.arg.plate
          ) {
            newHistoryReport.plateNumber =
              vehicleReport.vehicleSummary.plateNumber || action.meta.arg.plate;
          }
          if (vehicleReport.vehicleSummary.plateState) {
            newHistoryReport.plateState =
              vehicleReport.vehicleSummary.plateState;
          }

          state.mostRecentReport = newHistoryReport;
          state.vehicleReportHistory?.unshift(newHistoryReport);
          state.allReportHistory?.unshift(newHistoryReport);
        }
      })
      .addCase(pullVehicleReportAsync.rejected, state => {
        state.vehicleReport = undefined;
        state.vehicleReportStatus = "failed";
      })
      /* pullVehicleMarketAsync */
      .addCase(pullVehicleMarketEstimateAsync.pending, state => {
        state.vehicleMarketEstimationStatus = "loading";
      })
      .addCase(pullVehicleMarketEstimateAsync.fulfilled, (state, action) => {
        state.vehicleMarketEstimationStatus = "idle";

        let updatedVehicleValuation = action.payload.report
          .result as VehicleValuation;
        if (state.vehicleReport && updatedVehicleValuation) {
          state.vehicleReport = {
            ...state.vehicleReport,
            vehicleValuation: updatedVehicleValuation,
          };
        }
      })
      .addCase(pullVehicleMarketEstimateAsync.rejected, state => {
        state.vehicleMarketEstimationStatus = "failed";
      })
      /* pullNeighrborhoodReportByLocationAsync */
      .addCase(pullNeighborhoodReportByLocationAsync.pending, state => {
        state.neighborhoodReportStatus = "loading";
        state.specificReportNotes = undefined;
      })
      .addCase(
        pullNeighborhoodReportByLocationAsync.fulfilled,
        (state, action) => {
          state.neighborhoodReport = undefined;
          state.neighborhoodReportStatus = "idle";
          if (!action.payload.noResults) {
            let report = action.payload as NeighborhoodReport;
            state.neighborhoodReport = action.payload;

            // set report to propertyReport state

            //checking for opt outs and null results
            if (report) {
              let newHistoryReport: NeighborhoodReportHistory = {
                addressLine1: report.addressObject.address,
                city: report.addressObject.city,
                distance: 5,
                state: report.addressObject.state,
                zip: report.addressObject.zipCode,
                score: scoreGenerator(),
                type: "neighborhood",
                schema: "v2",
                offendersCount: report.offendersCount,
              };
              state.mostRecentReport = newHistoryReport;
              state.neighborhoodReportHistory?.unshift(newHistoryReport);
              state.allReportHistory?.unshift(newHistoryReport);
            }
          }
        },
      )
      .addCase(pullNeighborhoodReportByLocationAsync.rejected, state => {
        state.neighborhoodReport = undefined;
        state.neighborhoodReportStatus = "failed";
      })
      .addCase(pullNeighborhoodSearchAsync.pending, state => {
        state.neighborhoodSearchStatus = "loading";
      })
      .addCase(pullNeighborhoodSearchAsync.fulfilled, (state, action) => {
        state.neighborhoodSearch = undefined;
        state.neighborhoodSearchStatus = "idle";
        state.neighborhoodSearch = action.payload;
      })
      .addCase(pullNeighborhoodSearchAsync.rejected, state => {
        state.neighborhoodSearch = undefined;
        state.neighborhoodSearchStatus = "failed";
      })
      .addCase(submitComplaintAsync.pending, (state, action) => {
        state.submitComplaintStatus = "loading";
      })
      .addCase(submitComplaintAsync.rejected, state => {
        state.submitComplaintStatus = "failed";
      })
      .addCase(submitComplaintAsync.fulfilled, (state, action) => {
        state.submitComplaintStatus = "idle";
        if (state.phoneReport) {
          const updatedComplaints = [
            action.payload.submitPhoneComplaint.result[0],
            ...state.phoneReport.phoneComplaints.result,
          ];
          const updatedSummary = {
            ...state.phoneReport.phoneComplaints.summary,
            rplComplaintCounts:
              state.phoneReport.phoneComplaints.summary.rplComplaintCounts + 1,
          };
          state.phoneReport = {
            ...state.phoneReport,
            phoneComplaints: {
              ...state.phoneReport.phoneComplaints,
              result: updatedComplaints,
              summary: updatedSummary,
            },
          };
        }
      })
      /* getAllListsAsync */
      .addCase(getAllListsAsync.pending, state => {
        state.listStatus = "loading";
      })
      .addCase(getAllListsAsync.rejected, state => {
        state.listStatus = "failed";
      })
      .addCase(getAllListsAsync.fulfilled, (state, action) => {
        state.listStatus = "idle";
        state.reportLists = action.payload.result;
      })
      /* getSpecificListAsync */
      .addCase(getSpecificListAsync.pending, state => {
        state.listStatus = "loading";
      })
      .addCase(getSpecificListAsync.rejected, state => {
        state.listStatus = "failed";
      })
      .addCase(getSpecificListAsync.fulfilled, (state, action) => {
        state.listStatus = "idle";
        let foundFlag = false;
        let arrayOfLists = state.reportLists;

        const list = action.payload.result ? action.payload.result[0] : null;

        // if the list is found in currently saved list, update the list
        if (list) {
          arrayOfLists = state.reportLists?.map(reportList => {
            if (reportList.savedReportsListId === list.savedReportsListId) {
              foundFlag = true;
              return list;
            } else {
              return reportList;
            }
          });

          // if the list is not found in currently saved list, add it to the listArray
          if (!foundFlag) {
            arrayOfLists?.push(list);
          }

          state.reportLists = arrayOfLists;
        }
      })
      /* addReportToListAsync */
      .addCase(addReportToListAsync.pending, state => {
        state.listStatus = "loading";
      })
      .addCase(addReportToListAsync.rejected, state => {
        state.listStatus = "failed";
      })
      .addCase(addReportToListAsync.fulfilled, (state, action) => {
        // adding to state handled by getSpecificListAsync due to too many additional properties to manage / create
        state.listStatus = "idle";
      })
      /* removeReportFromListAsync */
      .addCase(removeReportFromListAsync.pending, state => {
        state.listStatus = "loading";
      })
      .addCase(removeReportFromListAsync.rejected, state => {
        state.listStatus = "failed";
      })
      .addCase(removeReportFromListAsync.fulfilled, (state, action) => {
        state.listStatus = "idle";
        state.reportLists = state.reportLists
          ?.map(reportList => {
            const containsSavedReport = reportList.savedReports.some(
              report => report.id === action.payload.savedReportId,
            );

            if (containsSavedReport) {
              const filteredReports = reportList.savedReports.filter(
                report => report.id !== action.payload.savedReportId,
              );
              if (filteredReports.length === 0) {
                return null;
              } else {
                return {
                  ...reportList,
                  lastActivityDate: `/Date(${Date.now()}-0000)/`,
                  savedReports: filteredReports,
                };
              }
            }
            return reportList;
          })
          // need to filter out nulls to allow type to work
          ?.filter((reportList): reportList is ReportList => !!reportList);
      })
      /* renameListAsync */
      .addCase(renameListAsync.pending, state => {
        state.listStatus = "loading";
      })
      .addCase(renameListAsync.rejected, state => {
        state.listStatus = "failed";
      })
      .addCase(renameListAsync.fulfilled, (state, action) => {
        state.listStatus = "idle";
        const timestamp = Date.now();
        const formattedDate = `/Date(${timestamp}-0000)/`;
        // map over lists and upate only the list name of the list that was renamed
        state.reportLists = state.reportLists?.map(reportList => {
          if (
            reportList.savedReportsListId === action.payload.savedReportsListId
          ) {
            return {
              ...reportList,
              listName: action.payload.listName,
              lastActivityDate: formattedDate,
            };
          } else {
            return reportList;
          }
        });
      })
      /* deleteListAsync */
      .addCase(deleteListAsync.pending, state => {
        state.listStatus = "loading";
      })
      .addCase(deleteListAsync.rejected, state => {
        state.listStatus = "failed";
      })
      .addCase(deleteListAsync.fulfilled, (state, action) => {
        state.listStatus = "idle";
        // filter out the list that was deleted
        state.reportLists = state.reportLists?.filter(
          reportList =>
            reportList.savedReportsListId !== action.payload.savedReportsListId,
        );
      })
      /* getAllReportsWithNotesAsync */
      .addCase(getAllReportsWithNotesAsync.pending, state => {
        state.notesStatus = "loading";
      })
      .addCase(getAllReportsWithNotesAsync.rejected, state => {
        state.notesStatus = "failed";
        state.specificReportNotes = undefined;
      })
      .addCase(getAllReportsWithNotesAsync.fulfilled, (state, action) => {
        state.notesStatus = "idle";
        state.allReportNotesList = action.payload.result;
      })
      /* getSpecificReportNotesAsync */
      .addCase(getSpecificReportNotesAsync.pending, state => {
        state.notesStatus = "loading";
        // state.specificReportNotes = undefined;
      })
      .addCase(getSpecificReportNotesAsync.rejected, state => {
        state.notesStatus = "failed";
        state.specificReportNotes = undefined;
      })
      .addCase(getSpecificReportNotesAsync.fulfilled, (state, action) => {
        state.notesStatus = "idle";
        state.specificReportNotes = action.payload.result;
      })
      /* addReportNoteAsync */
      .addCase(addReportNoteAsync.pending, state => {
        state.notesStatus = "loading";
      })
      .addCase(addReportNoteAsync.rejected, state => {
        state.notesStatus = "failed";
      })
      .addCase(addReportNoteAsync.fulfilled, (state, action) => {
        state.notesStatus = "idle";
        // update lastActivityDate and reportNotesCount in allReportNotes

        if (state.specificReportNotes && state.specificReportNotes[0]) {
          let reportNoteListId =
            state.specificReportNotes?.[0].reportNotesListId;
          if (reportNoteListId) {
            state.allReportNotesList = state.allReportNotesList?.map(
              reportWithNote =>
                reportWithNote.id === reportNoteListId
                  ? {
                      ...reportWithNote,
                      lastActivityDate: `/Date(${Date.now()}-0000)/`,
                      reportNotesCount: reportWithNote.reportNotesCount + 1,
                    }
                  : reportWithNote,
            );
          }
        }
      })
      /* removeReportNoteAsync */
      .addCase(removeReportNoteAsync.pending, state => {
        state.notesStatus = "loading";
      })
      .addCase(removeReportNoteAsync.rejected, state => {
        state.notesStatus = "failed";
      })
      .addCase(removeReportNoteAsync.fulfilled, (state, action) => {
        state.notesStatus = "idle";
        let reportNoteId = action.payload.reportNoteId;
        let reportNoteListId = state.specificReportNotes?.find(
          note => note.id === reportNoteId,
        )?.reportNotesListId;
        state.specificReportNotes = state.specificReportNotes?.filter(
          note => note.id !== action.payload.reportNoteId,
        );
        // update lastActivityDate and reportNotesCount in allReportNotesList
        state.allReportNotesList = state.allReportNotesList?.map(
          reportWithNote =>
            reportWithNote.id === reportNoteListId
              ? {
                  ...reportWithNote,
                  lastActivityDate: `/Date(${Date.now()}-0000)/`,
                  reportNotesCount: reportWithNote.reportNotesCount - 1,
                }
              : reportWithNote,
        );
      })
      /* updateReportNoteAsync */
      .addCase(updateReportNoteAsync.pending, state => {
        state.notesStatus = "loading";
      })
      .addCase(updateReportNoteAsync.rejected, state => {
        state.notesStatus = "failed";
      })
      .addCase(updateReportNoteAsync.fulfilled, (state, action) => {
        state.notesStatus = "idle";
        let reportNoteId = action.payload.reportNoteId;
        let updatedNote = action.payload.note;
        let reportNoteListId = state.specificReportNotes?.find(
          note => note.id === reportNoteId,
        )?.reportNotesListId;

        // update note and dateModified in specificReportNotes
        state.specificReportNotes = state.specificReportNotes?.map(note =>
          note.id === reportNoteId
            ? {
                ...note,
                note: updatedNote,
                dateModified: `/Date(${Date.now()}-0000)/`,
              }
            : note,
        );
        // update lastActivityDate in allReportNotesList
        state.allReportNotesList = state.allReportNotesList?.map(
          reportWithNote =>
            reportWithNote.id === reportNoteListId
              ? {
                  ...reportWithNote,
                  lastActivityDate: `/Date(${Date.now()}-0000)/`,
                }
              : reportWithNote,
        );
      });
  },
});

/* actions */
export const {
  setPeopleReportMonitoringModal,
  setNeighborhoodMapModal,
  setNeighborhoodMapModalContent,
  setSiteJabberShown,
  setPreSiteJabberModalShown,
  setShowSiteJabberFromFeedback,
  updateNeighborhoodAiUsed,
  updateNeighborhoodAiVoted,
  clearSpecificReportNotes,
  clearPeopleTeaserReports,
  setPreForeclosureSearchType,
  setVehicleNoResultsModalShown,
  setPropertyReportPreForeclosureData,
  setPropertyReportRecentSalesData,
} = reportsSlice.actions;

/* Getters */
export const selectReportStatus = (state: RootState) => state.reports.status;
export const selectReportHistoryStatus = (state: RootState) =>
  state.reports.reportHistoryStatus;
export const selectPeopleReportStatus = (state: RootState) =>
  state.reports.peopleReportStatus;
export const selectPhoneReportStatus = (state: RootState) =>
  state.reports.phoneReportStatus;
export const selectPeopleSearchStatus = (state: RootState) =>
  state.reports.peopleSearchStatus;
export const selectPropertyReportStatus = (state: RootState) =>
  state.reports.propertyReportStatus;
export const selectSocialSearchStatus = (state: RootState) =>
  state.reports.socialSearchStatus;
export const selectVehicleReportStatus = (state: RootState) =>
  state.reports.vehicleReportStatus;
export const selectNeighborhoodReportStatus = (state: RootState) =>
  state.reports.neighborhoodReportStatus;
export const selectNeighborhoodSearchStatus = (state: RootState) =>
  state.reports.neighborhoodSearchStatus;
export const selectPreForeclosureSearchStatus = (state: RootState) =>
  state.reports.preForeclosureSearchStatus;
export const selectRecentSaleSearchStatus = (state: RootState) =>
  state.reports.recentSaleSearchStatus;
export const selectRentDataStatus = (state: RootState) =>
  state.reports.rentDataStatus;
export const selectAiStatus = (state: RootState) => state.reports.aiStatus;
export const selectListStatus = (state: RootState) => state.reports.listStatus;
export const selectNotesStatus = (state: RootState) =>
  state.reports.notesStatus;
export const selectRentData = (state: RootState) => state.reports.rentData;
export const selectAllReportHistory = (state: RootState) =>
  state.reports.allReportHistory;
export const selectPeopleReportHistory = (state: RootState) =>
  state.reports.peopleReportHistory;
export const selectPhoneReportHistory = (state: RootState) =>
  state.reports.phoneReportHistory;
export const selectPropertyReportHistory = (state: RootState) =>
  state.reports.propertyReportHistory;
export const selectSocialReportHistory = (state: RootState) =>
  state.reports.socialReportHistory;
export const selectNeighborhoodReportHistory = (state: RootState) =>
  state.reports.neighborhoodReportHistory;
export const selectVehicleReportHistory = (state: RootState) =>
  state.reports.vehicleReportHistory;
export const selectMostRecentReport = (state: RootState) =>
  state.reports.mostRecentReport;
export const selectIsReportHistoryEmpty = (state: RootState) =>
  state.reports.isReportHistoryEmpty;
export const selectTeaserReports = (state: RootState) =>
  state.reports.peopleTeaserReports;
export const selectPeopleReport = (state: RootState) =>
  state.reports.peopleReport;
export const selectPeopleReportMonitoringDifferences = (state: RootState) =>
  state.reports.peopleReportMonitoringDifferences;
export const selectPropertyReport = (state: RootState) =>
  state.reports.propertyReport;
export const selectPropertyReportMonitoringDifferences = (state: RootState) =>
  state.reports.propertyReportMonitoringDifferences;
export const selectSocialReport = (state: RootState) =>
  state.reports.socialReport;
export const selectPhoneReport = (state: RootState) =>
  state.reports.phoneReport;
export const selectVehicleReport = (state: RootState) =>
  state.reports.vehicleReport;
export const selectNeighborhoodReport = (state: RootState) =>
  state.reports.neighborhoodReport;
export const selectNeighborhoodSearch = (state: RootState) =>
  state.reports.neighborhoodSearch;
export const selectPeopleReportMonitoringModal = (state: RootState) =>
  state.reports.peopleReportMonitoringModal;
export const selectSiteJabberShown = (state: RootState) =>
  state.reports.siteJabberShown;
export const selectPreSiteJabberModalShown = (state: RootState) =>
  state.reports.preSiteJabberModalShown;
export const selectShowSiteJabberFromFeedback = (state: RootState) =>
  state.reports.showSiteJabberFromFeedback;
export const selectNeighborhoodMapModal = (state: RootState) =>
  state.reports.neighborhoodMapModal;
export const selectNeighborhoodMapModalContent = (state: RootState) =>
  state.reports.neighborhoodMapModalContent;
export const selectPropertyReportTrackingFlags = (state: RootState) =>
  state.reports.propertyReportTrackingFlags;
export const selectReportNotes = (state: RootState) =>
  state.reports.specificReportNotes;
export const selectVehicleMarketEstimationStatus = (state: RootState) =>
  state.reports.vehicleMarketEstimationStatus;
export const selectVehicleNoResultsModalShown = (state: RootState) =>
  state.reports.vehicleNoResultsModalShown;
export const selectPreForeclosureData = (state: RootState) =>
  state.reports.preForeclosureData;
export const selectPropertyReportPreForeclosureData = (state: RootState) =>
  state.reports.propertyReportPreForeclosureData;
export const selectPropertyReportRecentSalesData = (state: RootState) =>
  state.reports.propertyReportRecentSalesData;

export const selectAccountMetrics = createSelector(
  [
    (state: RootState) => state.monitoring.monitoredReports,
    (state: RootState) => state.monitoring.propertyMonitoredReports,
    (state: RootState) => state.monitoring.neighborhoodMonitoringAreas,
    (state: RootState) => state.reports.reportLists,
    (state: RootState) => state.reports.allReportNotesList,
  ],
  (
    monitoredReports,
    propertyMonitoredReports,
    neighborhoodMonitoringAreas,
    reportLists,
    allReportNotesList,
  ) => {
    return {
      currentNumberOfReportsMonitored:
        (monitoredReports?.length || 0) +
        (propertyMonitoredReports?.length || 0) +
        (neighborhoodMonitoringAreas?.length || 0),
      currentNumberOfLists: reportLists?.length || 0,
      CurrentNumberOfReportsAddedToLists:
        reportLists?.reduce(
          (acc, list) => acc + (list.savedReports?.length || 0),
          0,
        ) || 0,
      currentNumberOfNotes: allReportNotesList?.length || 0,
    };
  },
);

const selectReportParam = (
  _state: RootState,
  report: CombinedReports | undefined,
) => report;

export const selectReportLists = (state: RootState) =>
  state.reports.reportLists;

export const selectAllReportNotesList = (state: RootState) =>
  state.reports.allReportNotesList;

export const selectIfReportIsOnList = createSelector(
  [selectReportLists, selectReportParam],
  (reportLists, report) => {
    const { foundFlag, list, reportId } = checkIfReportIsOnList(
      { reports: { reportLists } } as RootState,
      report,
    );
    return { foundFlag, list, reportId };
  },
);

export default reportsSlice.reducer;
